import { TextField } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Standalone text filter field
 * - Input changes are debounced
 * - Resets page number to 1 on filter change if withPagination is true
 */
export const TextFilter = (props) => {
    const {
        filterKey,
        searchParams,
        setSearchParams,
        resetPageNumber,
        withPagination,
        placeholder = "",
        debounceTimeout = 600,
    } = props;

    const [localFilterValue, setLocalFilterValue] = useState(searchParams.get(filterKey) || "");
    const debounceTimerRef = useRef(null);

    // Sync state with prop change
    useEffect(() => {
        setLocalFilterValue(searchParams.get(filterKey) || "");
    }, [searchParams, filterKey]);

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    const updateSearchParams = useCallback(
        (newFilterValue) => {
            const newSearchParams = new URLSearchParams(searchParams.toString());

            if (newFilterValue) {
                newSearchParams.set(filterKey, newFilterValue);
            } else {
                newSearchParams.delete(filterKey);
            }

            if (withPagination) {
                newSearchParams.set("page", "1");
            }
            setSearchParams(newSearchParams);

            if (withPagination) {
                resetPageNumber();
            }
        },
        [filterKey, searchParams, setSearchParams],
    );

    const handleChange = (newFilterValue) => {
        setLocalFilterValue(newFilterValue);

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(
            () => updateSearchParams(newFilterValue),
            debounceTimeout,
        );
    };

    return (
        <TextField
            value={localFilterValue}
            onChange={(e) => handleChange(e.target.value)}
            variant="outlined"
            fullWidth
            size="small"
            placeholder={placeholder}
        />
    );
};
